/**
 * QueryBuilder: Universal GraphQL query builder for Directus and general use.
 * Supports nested fields, filters, joins, sorting, aliasing, limits, offsets, and fully stringified output.
 * All types are documented for autocompletion and IDE support.
 */

/**
 * @typedef {object} QueryFilter - Filter object (e.g. { status: { _eq: "active" } })
 * @typedef {object} QueryOptions
 * @property {QueryFilter=} filter - Filter object for WHERE conditions
 * @property {string[]=} sort - Sorting fields (e.g. ["-created_at"])
 * @property {number=} limit - Limit result count
 * @property {number=} offset - Offset for pagination
 * @property {boolean=} preserve - Optional preserve state
 * @property {(item: any) => any=} map - Optional transformation map function
 *
 * @typedef {object} QueryDefinition
 * @property {string} name - Table or operation name
 * @property {any[]} fields - Fields to fetch (flat or nested)
 * @property {QueryOptions} options - Query options (filter, sort, etc.)
 * @property {string} alias - Optional alias for result
 */

export class QueryBuilder {
  constructor(name) {
    this.query = {
      name,
      fields: [],
      options: {},
      alias: 'data',
    };
  }

  select(fields) {
    this.query.fields = fields;
    return this;
  }

  join(nestedFields) {
    this.query.fields.push(...nestedFields);
    return this;
  }

  where(filter) {
    this.query.options.filter = filter;
    return this;
  }

  whereDynamic(inputFilter) {
    const converted = {};
    for (const [key, val] of Object.entries(inputFilter)) {
      if (val === undefined || val === null) continue;
      converted[key] = { _eq: val };
    }
    return this.where(converted);
  }

  inject(fn) {
    this._injectionFn = fn;
    return this;
  }

  applyInjections() {
    if (typeof this._injectionFn === 'function') {
      const shared = this._injectionFn();
      this.whereDynamic(shared);
    }
    return this;
  }

  orWhere(conditions) {
    this.query.options.filter = {
      _or: conditions,
    };
    return this;
  }

  orderBy(sort) {
    this.query.options.sort = sort;
    return this;
  }

  take(limit) {
    this.query.options.limit = limit;
    return this;
  }

  skip(offset) {
    this.query.options.offset = offset;
    return this;
  }

  as(alias) {
    this.query.alias = alias;
    return this;
  }

  map(mapFn) {
    this.query.options.map = mapFn;
    return this;
  }

  preserve(preserve = true) {
    this.query.options.preserve = preserve;
    return this;
  }

  build() {
    this.applyInjections();
    return this.query;
  }

  toGraphQLString() {
    this.applyInjections();
    const { name, fields, options, alias } = this.query;

    const buildFields = (fields) =>
      fields
        .map((f) => {
          if (typeof f === 'string') return f;
          const key = Object.keys(f)[0];
          const value = f[key];

          // Handle nested field with filter
          if (value && typeof value === 'object' && value.filter && value.fields) {
            const args = buildArgs({ filter: value.filter });
            return `${key}${args} { ${buildFields(value.fields)} }`;
          }

          // Handle regular nested fields
          if (Array.isArray(value)) {
            return `${key} { ${buildFields(value)} }`;
          }

          return `${key} { ${buildFields([value])} }`;
        })
        .join(' ');

    const buildArgs = (args) => {
      const validEntries = Object.entries(args || {}).filter(([_, v]) =>
        Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null
      );
      if (!validEntries.length) return '';
      return (
        '(' +
        validEntries
          .map(([k, v]) => `${k}: ${JSON.stringify(v).replace(/\"([^\"]+)\":/g, '$1:')}`)
          .join(', ') +
        ')'
      );
    };

    const args = buildArgs({
      filter: options.filter,
      sort: options.sort,
      limit: options.limit,
      offset: options.offset,
    });

    return `query { ${alias}: ${name}${args} { ${buildFields(fields)} } }`;
  }
}


// WEBPACK FOOTER //
// ./src/app/lib/directus-sdk/query-builder.js